<template>
  <div class="videos-page">
    <div class="bg-decoration">
      <div class="orb orb-1"></div>
      <div class="orb orb-2"></div>
      <div class="orb orb-3"></div>
    </div>

    <div class="content-wrapper">
      <aside class="sidebar">
        <nav class="nav-card">
          <div class="nav-logo">
            <div class="logo-icon">
              <img :src="logo2Image" alt="" class="logo2-icon" />
            </div>
            <div class="brand-copy">
              <h1 class="brand-name">树苗英语</h1>
              <span class="brand-subtitle">TREE SAPLING</span>
            </div>
          </div>

          <ul class="nav-list">
            <li v-for="item in navItems" :key="item.id">
              <a
                href="#"
                @click.prevent="handleNavItemClick(item)"
                :class="['nav-item', { active: activeNav === item.id }]"
              >
                <font-awesome-icon :icon="item.icon" class="nav-icon" />
                <span>{{ item.label }}</span>
              </a>
            </li>
          </ul>
          <div class="nav-menu-visual" aria-hidden="true">
            <img :src="menuBgImage" alt="" class="nav-menu-image" />
          </div>
        </nav>
      </aside>

      <main class="page-shell">

      <div class="mobile-topbar" :class="{ 'is-scrolled': isTopbarScrolled }">
        <button type="button" class="mobile-back-btn" @click="handleGoBack" aria-label="返回上一页">
          <font-awesome-icon icon="chevron-left" />
        </button>
        <h1 class="mobile-topbar-title">视频</h1>
        <span class="mobile-topbar-spacer" aria-hidden="true"></span>
      </div>
      <div class="mobile-topbar-offset" aria-hidden="true"></div>

      <div class="video-sticky-controls">
      <section class="search-section">
        <div class="search-box">
          <font-awesome-icon icon="search" class="search-icon" />
          <input
            v-model.trim="searchKeyword"
            type="text"
            class="search-input"
            placeholder="请输入关键字搜索"
            aria-label="搜索视频"
            @keyup.enter="handleSearch"
          />
          <button type="button" class="search-button" aria-label="搜索" title="搜索" @click="handleSearch">
            <span>搜索</span>
          </button>
        </div>
      </section>

      <section class="filters-section">
        <div ref="filtersMainRef" class="filters-main">
          <button
            v-for="category in categoryList"
            :key="category.id"
            type="button"
            class="filter-chip"
            :data-filter-id="category.id"
            :class="{ active: activeFilter === category.id }"
            @click="selectFilter(category.id, category.value, category.type)"
          >
            {{ category.label }}
          </button>
        </div>

        <button type="button" class="toggle-btn" @click="toggleMoreFilters">
          <font-awesome-icon icon="chevron-down" :class="{ expanded: isFilterMenuOpen }" />
        </button>
      </section>
      </div>

      <section class="video-grid">
        <template v-if="isVideoListLoading">
          <article
            v-for="index in videoSkeletonCount"
            :key="`video-skeleton-${index}`"
            class="video-card video-skeleton-card"
            aria-hidden="true"
          >
            <el-skeleton animated>
              <template #template>
                <div class="video-cover">
                  <div class="cover-frame video-skeleton-cover">
                    <el-skeleton-item variant="circle" class="video-skeleton-cover-inner" />
                  </div>
                </div>
                <el-skeleton-item variant="text" class="video-skeleton-title" />
                <el-skeleton-item variant="text" class="video-skeleton-season" />
              </template>
            </el-skeleton>
          </article>
        </template>

        <article
          v-else
          v-for="video in videos"
          :key="video.id"
          class="video-card"
          :class="{ selected: selectedVideoId === video.id }"
          @click="handleVideoClick(video)"
        >
          <div
            class="video-cover"
          >
            <div class="cover-frame">
              <img :src="video.coverUrl" alt="" class="cover-image" />
            </div>
          </div>

          <h3 class="video-title">{{ video.name }}</h3>

          <p class="video-season">共{{ video.totalSeason }}季</p>
        </article>

        <div v-if="!isVideoListLoading && videos.length === 0" class="empty-state">
          <p class="empty-title">没有找到匹配的视频</p>
          <p class="empty-desc">试试更换关键词或分类筛选</p>
        </div>
      </section>
      </main>
    </div>

    <Transition name="sheet-fade">
      <div v-if="isFilterMenuOpen" class="sheet-mask" @click="closeFilterMenu">
        <section class="sheet-panel" @click.stop>
          <div class="sheet-handle"></div>
          <h3 class="sheet-title">选择更多筛选</h3>

          <button
            v-for="filter in categoryList"
            :key="filter.id"
            type="button"
            class="sheet-option"
            :style="{ '--filter-color': filter.color }"
            :class="{ active: activeFilter === filter.id }"
            @click="selectFilterFromMenu(filter.id, filter.value, filter.type)"
          >
            <span>{{ filter.label }}</span>
            <span v-if="activeFilter === filter.id" class="sheet-check">已选</span>
          </button>

          <button type="button" class="sheet-cancel" @click="closeFilterMenu">取消</button>
        </section>
      </div>
    </Transition>

    <ParentPasswordModal ref="parentPasswordModalRef" />
  </div>
</template>

<script setup lang="ts">
import logo2Image from '../assets/logo2.png'
import menuBgImage from '../assets/menu_bg.jpg'
import ParentPasswordModal from '../components/ParentPasswordModal.vue'
import { nextTick, onMounted, onUnmounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getVideoSeriesPage, type VideoSeries } from '@/api/course-video'
import { getDictDataList } from '@/api/dict'

const route = useRoute()
const routeStage = Array.isArray(route.query.stage) ? route.query.stage[0] : route.query.stage
const hasRouteStage = routeStage != null && routeStage !== ''

const queryParams = ref({
  pageNo: 1,
  pageSize: 100,
  stage: routeStage,
  keyword: undefined as string | undefined,
  category: undefined as string | undefined,
})

const searchKeyword = ref('')
const activeFilter = ref('all')
const activeNav = ref('')
const isFilterMenuOpen = ref(false)
const selectedVideoId = ref(null)
const filtersMainRef = ref(null)
const isTopbarScrolled = ref(false)
const isVideoListLoading = ref(false)
const router = useRouter()
const parentPasswordModalRef = ref(null)

const navItems = [
  { id: 'home', label: '首页', icon: 'home' },
  { id: 'history', label: '观看历史', icon: 'play-circle' },
  { id: 'favorites', label: '宝贝收藏', icon: 'heart' },
  { id: 'settings', label: '偏好设置', icon: 'cog' },
  { id: 'parent', label: '家长模式', icon: 'user-shield' },
]


const categoryList = ref<any>([])
const videos = ref<VideoSeries[]>([])
const videoSkeletonCount = 8
const stageFilterList = [
  { id: 'stage-1', value: '1', label: '0-3', type: 'stage' },
  { id: 'stage-2', value: '2', label: '3-6', type: 'stage' },
  { id: 'stage-3', value: '3', label: '6-9', type: 'stage' },
  { id: 'stage-4', value: '4', label: '9-12', type: 'stage' },
]

const syncFilterChipIntoView = () => {
  nextTick(() => {
    const container = filtersMainRef.value
    if (!container) {
      return
    }

    const activeChip = container.querySelector(
      `button[data-filter-id="${activeFilter.value}"]`
    )
    if (!activeChip) {
      return
    }

    activeChip.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center',
    })
  })
}

const selectFilter = (filterId, filterValue, filterType = 'category') => {
  activeFilter.value = filterId
  queryParams.value.pageNo = 1

  if (filterType === 'stage') {
    queryParams.value.stage = filterValue
    queryParams.value.category = undefined
  } else {
    queryParams.value.stage = routeStage
    queryParams.value.category = filterValue || undefined
  }

  getVideoList()
  syncFilterChipIntoView()
}

const toggleMoreFilters = () => {
  isFilterMenuOpen.value = !isFilterMenuOpen.value
}

const closeFilterMenu = () => {
  isFilterMenuOpen.value = false
}

const selectFilterFromMenu = (filterId, filterValue, filterType = 'category') => {
  selectFilter(filterId, filterValue, filterType)
  closeFilterMenu()
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/category')
}

const openParentPasswordModal = () => {
  parentPasswordModalRef.value?.open()
}

const handleNavItemClick = (item) => {
  if (item.id === 'home') {
    router.push('/')
    return
  }

  if (item.id === 'parent') {
    openParentPasswordModal()
    return
  }

  if (item.id === 'settings') {
    router.push('/preference')
    return
  }

  if (item.id === 'history' || item.id === 'favorites') {
    router.push({
      path: '/history',
      query: item.id === 'favorites' ? { tab: 'favorites' } : {}
    })
  }
}

const handlePageScroll = () => {
  isTopbarScrolled.value = window.scrollY > 12
}

const handleSearch = () => {
  queryParams.value.pageNo = 1
  queryParams.value.keyword = searchKeyword.value || undefined
  getVideoList()
}

const handleVideoClick = (video) => {
  selectedVideoId.value = video.id
  router.push({path: '/player', query: {seriesId: video.id}})
}

onMounted(() => {
  getCategoryList()
  getVideoList()
  window.addEventListener('scroll', handlePageScroll, { passive: true })
  handlePageScroll()
})

onUnmounted(() => {
  window.removeEventListener('scroll', handlePageScroll)
})

const getVideoList = async () => {
  isVideoListLoading.value = true
  try {
    const data = await getVideoSeriesPage(queryParams.value)
    videos.value = data?.list || []
    //total.value = data.total
  } catch {
    videos.value = []
  } finally {
    isVideoListLoading.value = false
  }
}

const getCategoryList = async () => {
  try {
    const data = await getDictDataList('course_video_category')
    categoryList.value = [
      { id: 'all', value: '', label: '全部', type: 'category' },
      ...(!hasRouteStage ? stageFilterList : []),
      ...(data || [])
    ]
  } catch {

  }
}

</script>

<style scoped>
.videos-page {
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
}
.videos-page {
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  font-family: 'Nunito', 'Source Han Sans CN', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.bg-decoration {
  position: fixed;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
  z-index: -3;
}

.bg-decoration::before {
  content: '';
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, #FAF8F5 0%, #F5F0EB 50%, #EDE7E0 100%);
  z-index: -3;
}

.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.5;
  z-index: -2;
}

.orb-1 {
  width: 500px;
  height: 500px;
  background: linear-gradient(135deg, #FFE8D6, #FFD4B8);
  top: -150px;
  right: -100px;
}

.orb-2 {
  width: 400px;
  height: 400px;
  background: linear-gradient(135deg, #E8F5E9, #C8E6C9);
  bottom: -100px;
  left: -100px;
}

.orb-3 {
  width: 350px;
  height: 350px;
  background: linear-gradient(135deg, #E3F2FD, #BBDEFB);
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  opacity: 0.3;
}

.content-wrapper {
  display: flex;
  align-items: stretch;
  width: 100%;
  min-height: 100vh;
  padding-left: 264px;
}

.sidebar {
  width: 264px;
  flex-shrink: 0;
  height: 100vh;
  overflow: hidden;
  background: #FFF9F5;
  box-shadow: 4px 0 24px rgba(0, 0, 0, 0.06);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 24;
}

.nav-card {
  height: 100%;
  min-height: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  position: relative;
}

.nav-logo {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 20px;
  border-bottom: 1px solid rgba(255, 138, 61, 0.15);
}

.nav-logo .logo-icon {
  width: 62px;
  height: 62px;
  background: #fff;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(140, 104, 68, 0.12);
}

.nav-logo .logo2-icon {
  display: block;
  width: 52px;
  height: 52px;
  object-fit: contain;
}

.brand-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.nav-logo .brand-name {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #2D3436;
  letter-spacing: 1px;
  line-height: 1.05;
}

.brand-subtitle {
  color: #a78b76;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 1.2px;
  line-height: 1;
}

.nav-list {
  list-style: none;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  gap: clamp(12px, 2vh, 20px);
  padding: clamp(12px, 2vh, 16px) 12px;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border-radius: 14px;
  color: #666;
  font-size: 15px;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  text-decoration: none;
}

.nav-item:hover {
  background: rgba(255, 138, 61, 0.08);
  color: #FF8A3D;
}

.nav-item.active {
  background: linear-gradient(135deg, #FFE4D4 0%, #FFD4C4 100%);
  color: #FF6B35;
  font-weight: 700;
  box-shadow: 0 4px 16px rgba(255, 138, 61, 0.2);
}

.nav-icon {
  font-size: 18px;
  width: 24px;
  text-align: center;
}

.nav-menu-visual {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  margin-top: auto;
  border-radius: 0;
  overflow: hidden;
  pointer-events: none;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.nav-menu-image {
  display: block;
  flex-shrink: 0;
  width: 100%;
  min-width: 100%;
  height: auto;
  object-fit: contain;
  object-position: center bottom;
}

.page-shell {
  flex: 1;
  min-width: 0;
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 24px 40px 40px;
}

.mobile-topbar {
  display: none;
}

.mobile-topbar-offset {
  display: none;
}

.mobile-back-btn {
  display: none;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  cursor: pointer;
  align-items: center;
  justify-content: center;
}

.mobile-topbar-title {
  flex: 1;
  min-width: 0;
  margin: 0;
  padding: 0 12px;
  color: #2d3436;
  font-size: 20px;
  font-weight: 800;
  line-height: 1;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.mobile-topbar-spacer {
  display: none;
}

.video-sticky-controls {
  position: fixed;
  top: 0;
  left: 264px;
  right: 0;
  z-index: 18;
  margin: 0;
  padding: 18px max(40px, calc((100vw - 264px - 1400px) / 2 + 40px)) 12px;
  background: rgba(248, 244, 238, 0.9);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.search-section {
  margin-bottom: 14px;
}

.search-box {
  position: relative;
  display: block;
}

.search-icon {
  position: absolute;
  left: 18px;
  top: 50%;
  transform: translateY(-50%);
  color: #b8b8b8;
  font-size: 15px;
  z-index: 1;
  pointer-events: none;
}

.search-input {
  width: 100%;
  height: 58px;
  border-radius: 16px;
  border: 1px solid #e3dfd8;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  font-size: 16px;
  color: #303030;
  padding: 0 92px 0 46px;
  outline: none;
  transition: all 0.25s ease;
}

.search-input:focus {
  border-color: #ff8a3d;
  box-shadow: 0 0 0 3px rgba(255, 138, 61, 0.16);
}

.search-input::placeholder {
  color: #8e8e8e;
}

.search-button {
  position: absolute;
  top: 50%;
  right: 6px;
  height: 40px;
  min-width: 72px;
  padding: 0 16px;
  border: none;
  border-radius: 11px;
  background: linear-gradient(135deg, #ff8a3d 0%, #ff6b35 100%);
  color: #fff;
  font-size: 15px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(255, 138, 61, 0.26);
  cursor: pointer;
  transform: translateY(-50%);
  transition:
    transform 0.25s ease,
    box-shadow 0.25s ease,
    opacity 0.25s ease;
}

.search-button:hover {
  transform: translateY(calc(-50% - 1px));
  box-shadow: 0 12px 26px rgba(255, 138, 61, 0.34);
}

.search-button:active {
  transform: translateY(-50%);
  opacity: 0.92;
}

.filters-section {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 0;
}

.filters-main {
  display: flex;
  gap: 12px;
  flex: 1;
  min-width: 0;
  flex-wrap: nowrap;
  padding: 10px 0;
  margin: -10px 0;
  overflow-x: auto;
  overflow-y: hidden;
  -ms-overflow-style: none;
  scrollbar-width: none;
}

.filters-main::-webkit-scrollbar {
  width: 0;
  height: 0;
  display: none;
}

.filter-chip {
  appearance: none;
  -webkit-appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.8);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  color: #666;
  font-size: 16px;
  font-weight: 600;
  padding: 10px 22px;
  min-height: 40px;
  cursor: pointer;
  transition: all 0.25s ease;
  white-space: nowrap;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  outline: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.filter-chip:hover {
  background: rgba(255, 255, 255, 0.8);
  transform: translateY(-2px);
}

.filter-chip.active {
  border-radius: 999px;
  background: #ff8c42;
  border-color: #ff8c42;
  color: #fff;
  font-weight: 700;
  box-shadow: 0 8px 30px rgba(255, 140, 66, 0.35);
}

.toggle-btn {
  appearance: none;
  -webkit-appearance: none;
  width: 40px;
  height: 40px;
  border-radius: 999px;
  border: 1px solid #dad4ca;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  font-size: 14px;
  color: #626262;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.25s ease;
  flex-shrink: 0;
  outline: none;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
}

.toggle-btn:hover {
  color: #ff8a3d;
  border-color: #ffcfab;
}

.toggle-btn .expanded {
  transform: rotate(180deg);
}

.toggle-btn svg {
  transition: transform 0.25s ease;
}

.sheet-mask {
  position: fixed;
  inset: 0;
  z-index: 30;
  background: rgba(17, 21, 26, 0.34);
  display: flex;
  align-items: flex-end;
  justify-content: center;
  padding: 0 12px 12px;
}

.sheet-panel {
  width: min(720px, 100%);
  border-radius: 24px;
  border: 1px solid rgba(221, 214, 202, 0.95);
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 -12px 38px rgba(24, 25, 26, 0.16);
  padding: 10px 14px 14px;
}

.sheet-handle {
  width: 46px;
  height: 5px;
  border-radius: 999px;
  background: #ddd7cd;
  margin: 4px auto 10px;
}

.sheet-title {
  margin: 0 0 12px;
  text-align: center;
  font-size: 16px;
  color: #3b3b3b;
  font-weight: 700;
}

.sheet-option {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  border: 1px solid #e6e0d8;
  border-radius: 14px;
  background: #f8f6f2;
  color: #3f3f3f;
  font-size: 15px;
  font-weight: 600;
  padding: 13px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.25s ease;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}

.sheet-option + .sheet-option {
  margin-top: 10px;
}

.sheet-option:hover {
  border-color: #d8cec2;
  background: #f3efe9;
}

.sheet-option.active {
  border-color: #ffb788;
  background: linear-gradient(135deg, #ffeddc 0%, #ffe3d0 100%);
  color: #cf5c1f;
}

.sheet-check {
  font-size: 13px;
  font-weight: 700;
}

.sheet-cancel {
  appearance: none;
  -webkit-appearance: none;
  width: 100%;
  margin-top: 12px;
  border: none;
  border-radius: 14px;
  background: #ece7de;
  color: #4c4c4c;
  font-size: 15px;
  font-weight: 700;
  padding: 13px 14px;
  cursor: pointer;
  transition: all 0.25s ease;
  outline: none;
  -webkit-tap-highlight-color: transparent;
}

.sheet-cancel:hover {
  background: #e2dbd0;
}

.sheet-fade-enter-active,
.sheet-fade-leave-active {
  transition: opacity 0.25s ease;
}

.sheet-fade-enter-from,
.sheet-fade-leave-to {
  opacity: 0;
}

.sheet-fade-enter-active .sheet-panel,
.sheet-fade-leave-active .sheet-panel {
  transition: transform 0.25s ease;
}

.sheet-fade-enter-from .sheet-panel,
.sheet-fade-leave-to .sheet-panel {
  transform: translateY(100%);
}

.video-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  justify-items: stretch;
  margin-top: 140px;
  padding-bottom: 40px;
}

.video-card {
  width: 100%;
  background: #fff;
  border: 1px solid transparent;
  border-radius: 22px;
  padding: 10px 0 10px;
  box-shadow: none;
  cursor: pointer;
  transition: all 0.25s ease;
  overflow: hidden;
  text-align: center;
}

.video-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 14px 28px rgba(24, 25, 26, 0.08);
}

.video-card.selected {
  border-color: rgba(255, 138, 61, 0.75);
  box-shadow: 0 14px 30px rgba(255, 138, 61, 0.16);
}

.video-skeleton-card {
  cursor: default;
  pointer-events: none;
}

.video-skeleton-card :deep(.el-skeleton) {
  width: 100%;
}

.video-skeleton-card :deep(.el-skeleton__item) {
  background: linear-gradient(90deg, #f0ece6 25%, #f9f6f1 37%, #f0ece6 63%);
  background-size: 400% 100%;
}

.video-cover {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 0.72;
  background: transparent;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.video-skeleton-cover {
  box-shadow: none;
  background: transparent;
}

.video-skeleton-cover-inner {
  width: 100%;
  height: 100%;
}

.cover-frame {
  width: min(68%, 156px);
  aspect-ratio: 1;
  border-radius: 50%;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.94);
  box-shadow: 0 12px 28px rgba(24, 25, 26, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
}

.cover-image {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.video-title {
  margin: 4px 14px 4px;
  font-size: clamp(16px, 1.45vw, 24px);
  line-height: 1.2;
  color: #2f2f2f;
  font-weight: 700;
  height: 1.2em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.video-skeleton-title {
  display: block;
  width: calc(100% - 28px);
  height: 20px;
  margin: 8px auto 8px;
}

.video-season {
  margin: 0;
  font-size: 14px;
  line-height: 1.3;
  color: #8a7f73;
  font-weight: 600;
}

.video-skeleton-season {
  display: block;
  width: 44%;
  height: 16px;
  margin: 0 auto;
}

.empty-state {
  grid-column: 1 / -1;
  padding: 56px 16px;
  text-align: center;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.72);
  border: 1px solid rgba(222, 216, 206, 0.9);
}

.empty-title {
  font-size: 18px;
  color: #3a3a3a;
  margin-bottom: 8px;
  font-weight: 700;
}

.empty-desc {
  color: #8a8a8a;
  font-size: 14px;
}

@media (max-width: 1200px) {
  .content-wrapper {
    padding-left: 224px;
  }

  .sidebar {
    width: 224px;
  }

  .video-sticky-controls {
    left: 224px;
    padding-right: max(24px, calc((100vw - 224px - 1400px) / 2 + 24px));
    padding-left: max(24px, calc((100vw - 224px - 1400px) / 2 + 24px));
  }
}

@media (max-width: 992px) {
  .content-wrapper {
    padding-left: 208px;
  }

  .sidebar {
    width: 208px;
  }

  .video-sticky-controls {
    left: 208px;
    padding-right: 24px;
    padding-left: 24px;
  }

  .nav-logo {
    padding: 20px 20px;
    gap: 16px;
  }

  .nav-logo .logo-icon {
    width: 62px;
    height: 62px;
    font-size: 20px;
  }

  .nav-logo .logo2-icon {
    width: 52px;
    height: 52px;
  }

  .nav-logo .brand-name {
    font-size: 24px;
  }

  .nav-logo .brand-subtitle {
    font-size: 12px;
  }

  .nav-item {
    padding: 16px;
  }

  .page-shell {
    padding: 40px 24px 40px;
  }

  .video-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 768px),
  (min-width: 769px) and (max-width: 1024px) and (orientation: portrait),
  (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 540px) {
  .content-wrapper {
    display: block;
    min-height: 100vh;
    padding-left: 0;
  }

  .sidebar {
    display: none;
  }

  .page-shell {
    padding: 30px 16px 0;
  }

  .video-sticky-controls {
    top: calc(env(safe-area-inset-top, 0px) + 56px);
    left: 0;
    right: 0;
    margin: 0;
    padding: 10px 16px 12px;
    background: rgba(249, 247, 242, 0.96);
    z-index: 19;
  }

  .mobile-topbar {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    min-height: 56px;
    padding: calc(env(safe-area-inset-top, 0px) + 6px) 16px 6px;
    z-index: 20;
    transition: background-color 0.2s ease, box-shadow 0.2s ease;
  }

  .mobile-topbar.is-scrolled {
    background: rgba(249, 247, 242, 0.96);
    box-shadow: 0 8px 20px rgba(45, 52, 54, 0.08);
  }

  .mobile-topbar-offset {
    display: block;
    height: calc(env(safe-area-inset-top, 0px) + 56px);
  }

  .mobile-back-btn {
    display: inline-flex;
    position: absolute;
    left: 16px;
    top: calc(env(safe-area-inset-top, 0px) + 6px);
  }

  .search-input {
    height: 52px;
    font-size: 15px;
    padding-right: 86px;
  }

  .search-button {
    height: 36px;
    min-width: 68px;
    padding: 0 13px;
    border-radius: 11px;
    font-size: 13px;
  }

  .toggle-btn {
    width: 37px;
    height: 37px;
    font-size: 13px;
  }

  .filter-chip {
    font-size: 15px;
    padding: 9px 18px;
    min-height: 37px;
  }

  .sheet-mask {
    padding: 0;
  }

  .sheet-panel {
    width: 100%;
    border-radius: 22px 22px 0 0;
    border-bottom: none;
    padding: 10px 12px 16px;
  }

  .video-grid {
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    justify-content: stretch;
    justify-items: stretch;
    gap: 16px;
    margin-top: 136px;
    padding-bottom: calc(env(safe-area-inset-bottom, 0px) + 32px);
  }

  .video-card {
    width: 100%;
  }

  .cover-frame {
    width: min(72%, 136px);
  }

  .video-title {
    font-size: 20px;
    height: 1.2em;
  }

  .video-season {
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .page-shell {
    padding: 24px 14px 0;
  }

  .video-sticky-controls {
    padding-right: 14px;
    padding-left: 14px;
  }

  .mobile-topbar {
    padding-left: 14px;
    padding-right: 14px;
  }

  .mobile-back-btn {
    left: 14px;
  }

  .video-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .video-card {
    width: 100%;
  }

  .cover-frame {
    width: min(74%, 124px);
  }

  .filter-chip {
    font-size: 14px;
    padding: 8px 16px;
    min-height: 34px;
  }

  .search-input {
    padding-right: 78px;
  }

  .search-button {
    min-width: 62px;
    width: auto;
    padding: 0 12px;
  }

  .toggle-btn {
    width: 34px;
    height: 34px;
    font-size: 12px;
  }

  .sheet-option,
  .sheet-cancel {
    font-size: 14px;
    padding: 12px 13px;
  }

  .video-title {
    font-size: 17px;
    height: 1.2em;
  }

  .video-season {
    font-size: 12px;
  }
}
@media (min-width: 769px) {
  .nav-logo { padding: 20px 20px; gap: 16px; }
  .nav-logo .logo-icon { width: 62px; height: 62px; }
  .nav-logo .logo2-icon { width: 52px; height: 52px; }
  .nav-logo .brand-name { font-size: 24px; letter-spacing: 1px; line-height: 1.05; }
  .nav-logo .brand-subtitle { font-size: 12px; letter-spacing: 1.2px; line-height: 1; }
  .nav-item { padding: clamp(12px, 1.8vh, 18px) 18px; }
}

@media (min-width: 993px) and (max-width: 1200px) {
  .sidebar { width: 224px; }
}

@media (min-width: 769px) and (max-width: 992px) {
  .sidebar { width: 208px; }
}

@media (max-width: 768px),
  (min-width: 769px) and (max-width: 1024px) and (orientation: portrait),
  (hover: none) and (pointer: coarse) and (orientation: landscape) and (max-height: 540px) {
  .bg-decoration {
    position: absolute;
  }

  .orb {
    display: none;
  }

  .mobile-back-btn,
  .video-sticky-controls,
  .search-input,
  .filter-chip,
  .toggle-btn {
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }

  .nav-item,
  .search-input,
  .filter-chip,
  .toggle-btn,
  .sheet-option,
  .sheet-cancel,
  .video-card {
    transition-property: background-color, color, border-color, box-shadow, transform, opacity;
  }
}
</style>
